import { useEffect, useState } from "react";
import { Route, Switch, Link } from "wouter";

import { CheckPage } from "./pages/alle-check/page";
import { ResultPage } from "./pages/alle-check/result";
import { useLang, useT } from "./i18n/language";

const VIEW_MODE_KEY = "alle-check-view-mode";
type ViewMode = "list" | "stepper";

function readInitialMode(): ViewMode {
  if (typeof window === "undefined") return "stepper";
  const stored = window.localStorage.getItem(VIEW_MODE_KEY);
  if (stored === "list" || stored === "stepper") return stored;
  // Default: stap-voor-stap voor iedereen (desktop + mobiel). De lijstweergave
  // blijft beschikbaar via de toggle in de header.
  return "stepper";
}

function ViewToggle() {
  const t = useT();
  const [mode, setMode] = useState<ViewMode>(() => readInitialMode());

  const setAndBroadcast = (next: ViewMode) => {
    setMode(next);
    if (typeof window === "undefined") return;
    window.localStorage.setItem(VIEW_MODE_KEY, next);
    window.dispatchEvent(new CustomEvent("alle-check:view-mode", { detail: next }));
  };

  // Listen for changes coming from CheckPage (keeps both states in sync).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<ViewMode>).detail;
      if (detail === "list" || detail === "stepper") setMode(detail);
    };
    window.addEventListener("alle-check:view-mode", onChange);
    return () => window.removeEventListener("alle-check:view-mode", onChange);
  }, []);

  return (
    <div className="view-toggle" role="tablist" aria-label={t("Weergave kiezen")}>
      <button
        type="button"
        role="tab"
        aria-selected={mode === "list"}
        className={`view-toggle-btn ${mode === "list" ? "active" : ""}`}
        onClick={() => setAndBroadcast("list")}
      >
        {t("Lijstweergave")}
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === "stepper"}
        className={`view-toggle-btn ${mode === "stepper" ? "active" : ""}`}
        onClick={() => setAndBroadcast("stepper")}
      >
        {t("Stap-voor-stap")}
      </button>
    </div>
  );
}

// Tiny inline flag glyphs (SVG, never emoji). 4:3 rounded rects.
const FlagNL = () => (
  <svg width="18" height="13" viewBox="0 0 18 13" aria-hidden="true" style={{ borderRadius: 2, display: "block" }}>
    <rect width="18" height="13" fill="#FFFFFF" />
    <rect width="18" height="4.33" y="0" fill="#AE1C28" />
    <rect width="18" height="4.34" y="8.66" fill="#21468B" />
    <rect width="17.4" height="12.4" x="0.3" y="0.3" fill="none" stroke="rgba(19,26,32,0.15)" strokeWidth="0.6" rx="1.5" />
  </svg>
);

const FlagEN = () => (
  <svg width="18" height="13" viewBox="0 0 18 13" aria-hidden="true" style={{ borderRadius: 2, display: "block" }}>
    <rect width="18" height="13" fill="#012169" />
    <path d="M0 0 L18 13 M18 0 L0 13" stroke="#FFFFFF" strokeWidth="2.6" />
    <path d="M0 0 L18 13 M18 0 L0 13" stroke="#C8102E" strokeWidth="1.1" />
    <path d="M9 0 V13 M0 6.5 H18" stroke="#FFFFFF" strokeWidth="4.2" />
    <path d="M9 0 V13 M0 6.5 H18" stroke="#C8102E" strokeWidth="2.4" />
    <rect width="17.4" height="12.4" x="0.3" y="0.3" fill="none" stroke="rgba(19,26,32,0.15)" strokeWidth="0.6" rx="1.5" />
  </svg>
);

function LanguageToggle() {
  const { lang, setLang } = useLang();
  return (
    <div className="view-toggle" role="group" aria-label={lang === "nl" ? "Taal kiezen" : "Choose language"}>
      <button
        type="button"
        className={`view-toggle-btn ${lang === "nl" ? "active" : ""}`}
        onClick={() => setLang("nl")}
        aria-pressed={lang === "nl"}
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        <FlagNL />
        NL
      </button>
      <button
        type="button"
        className={`view-toggle-btn ${lang === "en" ? "active" : ""}`}
        onClick={() => setLang("en")}
        aria-pressed={lang === "en"}
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        <FlagEN />
        EN
      </button>
    </div>
  );
}

export default function App() {
  const t = useT();
  return (
    <div className="app-shell">
      <header className="page-header">
        <div className="header-inner">
          <div className="header-logo">{t("Quatt | All-E Recommender")}</div>
          <div className="header-right">
            <LanguageToggle />
            <ViewToggle />
            <Link href="/" className="header-action">{t("+ nieuwe aanbeveling")}</Link>
          </div>
        </div>
      </header>

      <main>
        <Switch>
          <Route path="/" component={CheckPage} />
          <Route path="/alle-check" component={CheckPage} />
          <Route path="/alle-check/r/:id" component={ResultPage} />
          <Route>
            <div className="container">
              <div className="results-layout">
                <div className="card">
                  <div className="section-header">
                    <span className="step-badge">!</span>
                    <h2 className="section-title">{t("Niet gevonden")}</h2>
                  </div>
                  <p className="card-subtitle">{t("De pagina die je zoekt bestaat niet.")}</p>
                  <Link href="/" className="btn-secondary" style={{ display: "inline-block", textDecoration: "none" }}>
                    {t("Terug naar de aanbeveling")}
                  </Link>
                </div>
              </div>
            </div>
          </Route>
        </Switch>
      </main>

      <footer className="page-footer">
        <div className="footer-inner">
          <div className="footer-text">{t("Indicatief advies. Definitieve geschiktheid volgt na een installatie-inspectie.")}</div>
          <div className="footer-version">Quatt</div>
        </div>
      </footer>
    </div>
  );
}
